import NiceModal from '@ebay/nice-modal-react';
import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import testingConstants from 'utils/testingConstants';

import ApiKeyRow, { ApiKeyRowVariant } from './ApiKeyRow';
import ApiKeysEmptyState from './ApiKeysEmptyState';
import ApiKeysLoadingSkeleton from './ApiKeysLoadingSkeleton';
import { ApiKeysListResult } from './useApiKeys';
import ApiKeyActionErrorModal from '../modals/ApiKeyActionErrorModal';

interface ApiKeysListProps {
  active: ApiKeysListResult;
  expired: ApiKeysListResult;
  /** True while the backend workspace UUID is still resolving — keeps the skeleton up. */
  isResolvingWorkspace: boolean;
  /** Backend workspace UUID; forwarded to rows for rotate/revoke (`null` while resolving). */
  workspaceUuid: string | null;
  /** Re-fetches both lists after a row-level rotate/revoke mutation. */
  onKeysChanged: () => void;
}

const ApiKeysList = ({ active, expired, isResolvingWorkspace, workspaceUuid, onKeysChanged }: ApiKeysListProps) => {
  const { t } = useTranslation();
  const [activeSubTab, setActiveSubTab] = useState<ApiKeyRowVariant>('active');
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Remembers the last error object that already opened the action-error modal,
  // per sub-tab, so a single failure shows the modal once (AC-03-12).
  const shownErrorsRef = useRef<Record<ApiKeyRowVariant, unknown>>({ active: null, expired: null });

  const current = activeSubTab === 'active' ? active : expired;
  const { items, isLoading, hasMore, loadMore, error, refresh } = current;

  const showSkeleton = (isLoading || isResolvingWorkspace) && !items.length;
  // 403 disabled / no-access states are handled one level up in ApiKeysTab. A
  // generic fetch failure on the visible stream opens the shared action-error
  // modal (effect below) instead of rendering a false empty state.
  const showEmpty = !showSkeleton && !error && !items.length;

  useEffect(() => {
    if (!error) {
      shownErrorsRef.current[activeSubTab] = null;
      return;
    }
    if (shownErrorsRef.current[activeSubTab] === error) return;
    shownErrorsRef.current[activeSubTab] = error;
    void NiceModal.show(ApiKeyActionErrorModal, { operation: 'list', onRetry: refresh });
  }, [error, activeSubTab, refresh]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore) loadMore();
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, isLoading, activeSubTab]);

  const subTabs: { id: ApiKeyRowVariant; label: string; testId: string }[] = [
    { id: 'active', label: t('workspaces_settings.api_keys.tabs.active'), testId: testingConstants.apiKeysActiveTab },
    {
      id: 'expired',
      label: t('workspaces_settings.api_keys.tabs.expired'),
      testId: testingConstants.apiKeysExpiredTab,
    },
  ];

  return (
    <div className="flex flex-col">
      <div className="flex gap-2">
        {subTabs.map((subTab) => (
          <button
            key={subTab.id}
            type="button"
            onClick={() => setActiveSubTab(subTab.id)}
            data-testid={subTab.testId}
            className={cx(
              'relative px-3 pb-2.5 text-sm',
              activeSubTab === subTab.id
                ? 'text-grayscale-black rounded-t-lg font-medium'
                : 'text-gray-70 hover:text-grayscale-black'
            )}
          >
            {subTab.label}
            {activeSubTab === subTab.id && (
              <span className="bg-primary-50 absolute inset-x-0 bottom-0 h-0.5 rounded-sm" aria-hidden />
            )}
          </button>
        ))}
      </div>

      <div
        className="border-gray-30 rounded-bl-2xl rounded-br-2xl rounded-tr-2xl flex flex-col border p-3"
        data-testid={testingConstants.apiKeysListPanel}
      >
        {showSkeleton && <ApiKeysLoadingSkeleton />}
        {showEmpty && <ApiKeysEmptyState />}
        {!showSkeleton && !showEmpty && items.length > 0 && (
          <div className="divide-gray-20 flex flex-col divide-y">
            {items.map((item) => (
              <ApiKeyRow
                key={item.id}
                item={item}
                variant={activeSubTab}
                workspaceUuid={workspaceUuid}
                onKeysChanged={onKeysChanged}
              />
            ))}
          </div>
        )}

        {isLoading && items.length > 0 && (
          <div className="flex justify-center py-2">
            <div className="border-gray-20 border-t-primary-50 h-5 w-5 animate-spin rounded-full border-2" />
          </div>
        )}

        <div ref={sentinelRef} className="h-1" />
      </div>
    </div>
  );
};

export default ApiKeysList;
